import { useState } from 'react';
import CustomButton from '../reusable/CustomButton';
import useErrorModal from '../hooks/useErrorModal';

interface SurveyLinksProps {
  surveyId: string;
  surveyUrl: string;
  languageUrls: { [language: string]: string };
  externalId: string;
}

const SurveyLinks = ({ surveyId, surveyUrl, languageUrls, externalId }: SurveyLinksProps) => {
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const handleCopyLink = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(label);
      setTimeout(() => {
        setCopiedUrl(null);
      }, 2000);
    } catch (error: any) {
      console.error('Error copying to clipboard:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to copy link to clipboard. Please try again.';
      handleOpenErrorModal(errorMessage, error as Error);
    }
  };

  return (
    <div className="survey-management-links-section" onClick={(e) => e.stopPropagation()}>
      <div className="survey-management-links-header">
        <span className="survey-management-links-title">📋 Survey Links:</span>
        <a
          href={`https://admin.typeform.com/form/${externalId}/create`}
          target="_blank"
          rel="noopener noreferrer"
          className="survey-management-external-link"
        >
          View in Typeform
        </a>
      </div>

      <div className="survey-management-links-list">
        <div className="survey-management-link-item">
          <span className="survey-management-link-label">• English (Primary):</span>
          <a
            href={surveyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="survey-management-link-url"
            title={surveyUrl}
          >
            {surveyUrl}
          </a>
          <CustomButton
            variant="text"
            buttonText={copiedUrl === 'English' ? 'Copied!' : 'Copy'}
            buttonAction={() => handleCopyLink(surveyUrl, 'English')}
            sx={{ fontSize: 'var(--font-size-12)', padding: '2px 6px', minWidth: 0, marginLeft: '8px' }}
          />
        </div>

        {Object.entries(languageUrls).map(([language, url]) => (
          <div key={language} className="survey-management-link-item">
            <span className="survey-management-link-label">• {language}:</span>
            <a href={url} target="_blank" rel="noopener noreferrer" className="survey-management-link-url" title={url}>
              {url}
            </a>
            <CustomButton
              variant="text"
              buttonText={copiedUrl === language ? 'Copied!' : 'Copy'}
              buttonAction={() => handleCopyLink(url, language)}
              sx={{ fontSize: 'var(--font-size-12)', padding: '2px 6px', minWidth: 0, marginLeft: '8px' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SurveyLinks;
